import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

export const THEMES = [
  {
    id: "royal",
    name: "Royal Emerald & Antique Gold",
    tagline: "Emerald rail, ivory linen canvas, gold leaf",
    swatch: ["#0A3431", "#D4AF37", "#F9F6EE"],
    mode: "light",
  },
  {
    id: "executive",
    name: "Executive Navy & Champagne",
    tagline: "Obsidian navy with champagne platinum",
    swatch: ["#0A192F", "#C5A880", "#F4F7FB"],
    mode: "light",
  },
  {
    id: "copper",
    name: "Slate Charcoal & Copper",
    tagline: "Industrial carbon with brushed copper",
    swatch: ["#1E2229", "#D97736", "#F2F4F7"],
    mode: "light",
  },
  {
    id: "minimal",
    name: "Minimalist Light & Cobalt",
    tagline: "Ice white with electric cobalt blue",
    swatch: ["#FFFFFF", "#1570EF", "#F8F9FA"],
    mode: "light",
  },
  {
    id: "obsidian",
    name: "Obsidian Dark & Electric Cyan",
    tagline: "OLED black with neon cyan glow",
    swatch: ["#0B0F17", "#00F2FE", "#1F2937"],
    mode: "dark",
  },
];

const STORAGE_KEY = "pramsia-theme";
const DEFAULT_THEME = "royal";
/** Earlier theme ids map onto the closest of the current five. */
const LEGACY = {
  heritage: "royal", regal: "royal", midnight: "executive", sapphire: "executive",
  cyber: "obsidian", forge: "copper",
};

const ThemeContext = createContext({ theme: DEFAULT_THEME, setTheme: () => {} });

const resolve = (value) => {
  const id = LEGACY[value] || value;
  return THEMES.some((t) => t.id === id) ? id : DEFAULT_THEME;
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => resolve(localStorage.getItem(STORAGE_KEY)));

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    const mode = THEMES.find((t) => t.id === theme)?.mode || "light";
    root.style.colorScheme = mode;
    root.classList.toggle("dark", mode === "dark");
  }, [theme]);

  const setTheme = useCallback((id) => {
    const next = resolve(id);
    setThemeState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(() => ({ theme, setTheme, themes: THEMES }), [theme, setTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
